import { useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export interface NavigationMessage {
  type:
    | 'NAVIGATE'
    | 'ROUTE_CHANGE'
    | 'NAVIGATE_TO_DEVELOPER'
    | 'NAVIGATE_TO_MARKETPLACE'
    | 'NAVIGATE_TO_MY_APPS';
  payload: {
    path?: string;
    search?: string;
    state?: any;
    appId?: string;
    replace?: boolean;
  };
}

export interface IframeNavigationSyncOptions {
  allowedOrigins: string[];
  iframeRef?: React.RefObject<HTMLIFrameElement>;
  onNavigationMessage?: (message: NavigationMessage) => void;
  debugMode?: boolean;
}

export const useIframeNavigationSync = (
  options: IframeNavigationSyncOptions,
) => {
  const { allowedOrigins, onNavigationMessage, debugMode = false } = options;

  const history = useHistory();
  const location = useLocation();

  const log = useCallback(
    (message: string, data?: any) => {
      if (debugMode) {
        if (data !== undefined) {
          console.log(`[IframeNavigationSync] ${message}`, data);
        } else {
          console.log(`[IframeNavigationSync] ${message}`);
        }
      }
    },
    [debugMode],
  );

  // Handle incoming navigation messages from iframe
  const handleNavigationMessage = useCallback(
    (event: MessageEvent<NavigationMessage>) => {
      // Validate origin
      const isValidOrigin = allowedOrigins.some(
        (origin) => origin === '*' || event.origin === origin,
      );

      if (!isValidOrigin) {
        log('Rejected message from invalid origin', event.origin);
        return;
      }

      const { type, payload } = event.data;
      log('Received navigation message', { type, payload });

      // Call custom handler if provided
      if (onNavigationMessage) {
        onNavigationMessage(event.data);
      }

      // Handle built-in navigation types
      switch (type) {
        case 'NAVIGATE':
          if (payload.path) {
            const url = payload.search
              ? `${payload.path}?${payload.search}`
              : payload.path;
            if (payload.replace) {
              history.replace(url, payload.state);
            } else {
              history.push(url, payload.state);
            }
          }
          break;

        case 'NAVIGATE_TO_DEVELOPER': {
          const searchParams = new URLSearchParams(location.search);
          const basePath = payload.appId
            ? '/developer/custom-app'
            : '/developer';
          if (payload.appId) {
            searchParams.set('appId', payload.appId);
          } else {
            searchParams.delete('appId');
          }
          const developerUrl = `${basePath}${
            searchParams.toString() ? `?${searchParams.toString()}` : ''
          }`;

          history.push(developerUrl);
          break;
        }

        case 'NAVIGATE_TO_MARKETPLACE': {
          history.push(`/marketplace${location.search}`);
          break;
        }

        case 'NAVIGATE_TO_MY_APPS': {
          history.push(`/my-apps${location.search}`);
          break;
        }

        default:
          log('Unknown navigation message type', type);
      }
    },
    [allowedOrigins, history, location, onNavigationMessage, log],
  );

  // Set up message listener
  useEffect(() => {
    window.addEventListener('message', handleNavigationMessage);
    log('Navigation sync initialized', { allowedOrigins });

    return () => {
      window.removeEventListener('message', handleNavigationMessage);
      log('Navigation sync cleanup');
    };
  }, [handleNavigationMessage, allowedOrigins, log]);

  return {
    currentRoute: `${location.pathname}${location.search}`,
  };
};
